import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { usePermissions } from '../contexts/PermissionsContext';
import { PortalModule } from '../components/Shared/ProtectedRoute';
import { ONBOARDING_URL } from '../authConfig';

interface Tool {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  available: boolean;
  requiredModule?: PortalModule;
  // Application externe habillée comme le portail, ouverte dans le même onglet
  external?: boolean;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const { permissions } = usePermissions();
  const firstName = accounts[0]?.name?.split(' ')[0] || 'Admin';

  const tools: Tool[] = [
    {
      title: 'Offboarding',
      description: 'Désactiver un compte M365, convertir la boîte mail, retirer les groupes',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      ),
      path: '/offboarding',
      available: true,
      requiredModule: 'offboarding',
    },
    {
      title: 'Étiquettes SOLUM',
      description: 'Suivi et gestion des étiquettes numériques ESL — export Odoo → SOLUM',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      ),
      path: '/esl-solum',
      available: true,
      requiredModule: 'eslSolum',
    },
    {
      title: 'Export images Odoo',
      description: 'Convertir les images base64 Odoo (image_1920) en fichiers JPG/PNG téléchargeables',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
        </svg>
      ),
      path: '/odoo-images',
      available: true,
      requiredModule: 'odooImages',
    },
    {
      title: 'Onboarding / Offboarding RH',
      description: "Demandes d'arrivée et de départ des collaborateurs, suivi RH et IT",
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
        </svg>
      ),
      path: ONBOARDING_URL,
      available: true,
      requiredModule: 'onboarding',
      external: true,
    },
    {
      title: 'Calendrier',
      description: 'Gérer les salles de réunion et les calendriers partagés',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      ),
      path: '/calendar',
      available: false,
    },
    {
      title: 'Gestion des absences',
      description: "Configurer les messages d'absence et les délégations",
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
        </svg>
      ),
      path: '/absences',
      available: false,
    },
  ];

  // Masquer les modules actifs auxquels l'utilisateur n'a pas accès
  const visibleTools = tools.filter((tool) => {
    if (!tool.available) return permissions.admin; // "Bientôt" réservé aux admins
    if (!tool.requiredModule) return true;
    return permissions[tool.requiredModule];
  });

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-ms-gray-800">Bonjour, {firstName}</h2>
        <p className="text-ms-gray-500 mt-1">
          Bienvenue sur le portail IT. Sélectionnez un outil pour commencer.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibleTools.map((tool) => (
          <button
            key={tool.path}
            onClick={() => {
              if (!tool.available) return;
              if (tool.external) window.location.href = tool.path;
              else navigate(tool.path);
            }}
            disabled={!tool.available}
            className={`text-left p-6 bg-white border rounded-lg transition-all ${
              tool.available
                ? 'border-ms-gray-200 hover:border-ms-blue hover:shadow-md cursor-pointer'
                : 'border-ms-gray-100 opacity-60 cursor-not-allowed'
            }`}
          >
            <div className={`mb-3 ${tool.available ? 'text-ms-blue' : 'text-ms-gray-400'}`}>
              {tool.icon}
            </div>
            <h3 className="text-base font-semibold text-ms-gray-800 mb-1">{tool.title}</h3>
            <p className="text-sm text-ms-gray-500">{tool.description}</p>
            {!tool.available && (
              <span className="inline-block mt-3 text-xs bg-ms-gray-100 text-ms-gray-400 px-2 py-1 rounded">
                Bientôt disponible
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Home;
